import { useEffect, useCallback, useState, useMemo, useRef, createContext, use } from 'react'
import { createPortal } from 'react-dom'
import BigNumber from 'bignumber.js'
import { ChainId, Currency, Price } from '@pancakeswap/sdk'
import { useWeb3React } from '@pancakeswap/wagmi'
import {
  Image,
  Heading,
  Toggle,
  Text,
  Button,
  ArrowForwardIcon,
  Flex,
  Link,
  Box,
  Farm as FarmUI,
  ScrollToTopButtonV2,
  Loading,
  SearchInput,
  Select,
  OptionProps,
  FlexLayout,
  PageHeader,
  NextLinkFromReactRouter,
  Input,
  HelpIcon,
} from '@pancakeswap/uikit'
import styled from 'styled-components'
import Page from 'components/Layout/Page'
import { useFarms, usePollFarmsWithUserData, usePriceCakeBusd } from 'state/farms/hooks'
import { useCakeVaultUserData } from 'state/pools/hooks'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import { DeserializedFarm } from '@pancakeswap/farms'
import { useTranslation } from '@pancakeswap/localization'
import { getFarmApr } from 'utils/apr'
import orderBy from 'lodash/orderBy'
import { latinise } from 'utils/latinise'
import { useUserFarmStakedOnly, useUserFarmsViewMode } from 'state/user/hooks'
import { ViewMode } from 'state/user/actions'
import { useRouter } from 'next/router'
import ToggleView from 'components/ToggleView/ToggleView'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useBUSDPrice from 'hooks/useBUSDPrice'
import { qomTokens } from '@pancakeswap/tokens'
import AppWrapper from 'components/AppWrapper'
import TokenInput from 'components/TokenInput'
import { isMobile } from 'react-device-detect'
import { ethers } from 'ethers'
import { useCurrencyBalance } from 'state/wallet/hooks'
import auctionsABI from 'config/abi/auctions.json'
import { qom } from '@icecreamswap/constants/src/chains/qom'
import { useContractRead } from 'wagmi'
import useTheme from 'hooks/useTheme'

interface GuideBoxProps {
  isOpen: boolean
}

const ControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;

  justify-content: space-between;
  flex-direction: column;
  margin-bottom: 32px;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 16px 32px;
    margin-bottom: 0;
  }
`
const FarmFlexWrapper = styled(Flex)`
  flex-wrap: wrap;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-wrap: nowrap;
  }
`
const FarmH1 = styled(Heading)`
  font-size: 32px;
  margin-bottom: 8px;
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 64px;
    margin-bottom: 24px;
  }
`
const FarmH2 = styled(Heading)`
  font-size: 16px;
  margin-bottom: 8px;
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 24px;
    margin-bottom: 18px;
  }
`

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;

  ${Text} {
    margin-left: 8px;
  }
`

const LabelWrapper = styled.div`
  > ${Text} {
    font-size: 12px;
  }
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0px;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: auto;
    padding: 0;
  }
`

const ViewControls = styled.div`
  flex-wrap: wrap;
  justify-content: space-between;
  display: flex;
  align-items: center;
  width: 100%;

  > div {
    padding: 8px 0px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    justify-content: flex-start;
    width: auto;

    > div {
      padding: 0;
    }
  }
`

const StyledImage = styled(Image)`
  margin-left: auto;
  margin-right: auto;
  margin-top: 58px;
`

const FinishedTextContainer = styled(Flex)`
  padding-bottom: 32px;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  }
`

const FinishedTextLink = styled(Link)`
  font-weight: 400;
  white-space: nowrap;
  text-decoration: underline;
`

const Timer = styled(Flex)`
  font-size: 32px;
  flex-direction: row;
  align-items: center;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  }
  & > div {
    margin: 0 10px;
  }
`

const GuideBox = styled.div<GuideBoxProps>`
  background-color: ${({ theme }) => theme.colors.background}; /* Use background color from theme */
  color: ${({ theme }) => theme.colors.text}; /* Use text color from theme */
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 20px auto;
  font-family: Arial, sans-serif;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

  h2 {
    font-size: 24px;
    margin-bottom: 15px;
    text-align: center;
  }

  p {
    font-size: 16px;
    margin-bottom: 10px;
    line-height: 1.5;
  }

  ul {
    list-style-type: disc;
    padding-left: 20px;
    margin-bottom: 20px;
  }

  ul li {
    margin-bottom: 8px;
  }
`

interface HelpIconProps {
  scale?: 'small' | 'medium' | 'large' | 'xxl'
}

const scaleValues = {
  small: '16px',
  medium: '24px',
  large: '32px',
  xxl: '48px',
}

const NUMBER_OF_FARMS_VISIBLE = 12

function priceToBigNumber(price: Price<Currency, Currency>): BigNumber {
  return new BigNumber(price.toSignificant(6)) // Convert to a string with 6 significant digits and then to BigNumber
}

type TimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function calculateTimeLeft(startTime: number, endTime: number): TimeLeft | null {
  const currentTime = Math.floor(Date.now() / 1000) // Get current time in Unix timestamp (seconds)

  if (currentTime < startTime) {
    console.error('The start time is in the future.')
    return null
  }

  let timeLeftInSeconds = endTime - currentTime

  if (timeLeftInSeconds <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }

  const days = Math.floor(timeLeftInSeconds / (3600 * 24))
  timeLeftInSeconds %= 3600 * 24
  const hours = Math.floor(timeLeftInSeconds / 3600)
  timeLeftInSeconds %= 3600
  const minutes = Math.floor(timeLeftInSeconds / 60)
  const seconds = timeLeftInSeconds % 60

  return {
    days,
    hours,
    minutes,
    seconds,
  }
}

const Auctions: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname, query: urlQuery } = useRouter()
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const [token, setToken] = useState<Currency>()
  const [amount, setAmount] = useState('')
  const [projectName, setProjectName] = useState('')
  const [showAmountInput, setShowAmountInput] = useState(false) // State to manage the visibility of amount input
  const { theme, isDark } = useTheme()

  const cakePrice = usePriceCakeBusd()
  const qswapPrice = useBUSDPrice(qomTokens.qswap)
  const [_query, setQuery] = useState('')
  const normalizedUrlSearch = useMemo(() => (typeof urlQuery?.search === 'string' ? urlQuery.search : ''), [urlQuery])
  const query = normalizedUrlSearch && !_query ? normalizedUrlSearch : _query

  const { account } = useWeb3React()

  const [error, setError] = useState<string | null>(null)
  const qswapToken = qomTokens.qswap
  const qswapBalance = useCurrencyBalance(account ?? undefined, qswapToken)
  const { chainId: appChainId } = useActiveChainId()
  const { auctionAddress } = qom.auction
  const [accountTyped, setAccountTyped] = useState('')

  let qswapPriceAsBigNumber: BigNumber | undefined

  if (qswapPrice) {
    qswapPriceAsBigNumber = priceToBigNumber(qswapPrice)
  } else {
    qswapPriceAsBigNumber = new BigNumber(0) // Fallback if the price is unavailable
  }

  const handleSearch = () => {
    if (token?.symbol === qomTokens.qswap.symbol) {
      setShowAmountInput(true)
      setError(null)
    } else {
      setShowAmountInput(false)
      setError('Please select QSWAP token')
    }
  }

  useEffect(() => {
    if (token?.symbol !== qomTokens.qswap.symbol) {
      setShowAmountInput(false)
      setError(null)
    }
  }, [token, setShowAmountInput, setError])

  const { data, isError, isLoading } = useContractRead({
    addressOrName: auctionAddress,
    contractInterface: auctionsABI,
    functionName: 'eligible',
    chainId: appChainId,
    args: [account || accountTyped],
    enabled: !!accountTyped,
  })

  const startTime = 1724367928
  const endTime = 1824367928

  return (
    // <>
    //   <PageHeader
    //     background={
    //       isDark
    //         ? 'linear-gradient(135deg, #000000 0%, #141317 100%)'
    //         : 'linear-gradient(160deg, #0093E9 0%, #80D0C7 100%)'
    //     }
    //   >
    //     <FarmFlexWrapper justifyContent="center">
    //       <Box>
    //         <FarmH1 as="h1" scale="xxl" color="secondary" mb="24px">
    //           {t('AUCTIONS')}
    //         </FarmH1>
    //         <FarmH2 scale="lg" color="text">
    //           {t('COMING SOON.')}
    //         </FarmH2>
    //         <CountdownTimer startTime={startTime} endTime={endTime} />
    //       </Box>
    //     </FarmFlexWrapper>
    //   </PageHeader>
    //   <Page>
    //     <FAQ />
    //     <Flex flexDirection="column" gap="0.75em">
    //       <Text color="textSubtle" fontSize="24px" textAlign="start">
    //         Select QSWAP Token
    //       </Text>
    //       <Text color="textSubtle" fontSize="24px" textAlign="start">
    //         Create Bids
    //       </Text>
    //       <Text color="textSubtle" fontSize="24px" textAlign="start">
    //         Wallet Balance: {qswapBalance?.toSignificant(6) ?? '0'} QSWAP
    //       </Text>{' '}
    //       {/* Display the wallet balance */}
    //       <Flex alignItems="start" gap="1em" flexDirection={isMobile ? 'column' : 'row'} justifyContent="stretch">
    //         <Input as="div" padding="0px" display="flex" alignItems="center">
    //           <TokenInput currency={token} onCurrencySelect={setToken} showNative={false} showCommonBases={false} />
    //         </Input>
    //         <Button onClick={handleSearch} height="40px" width={isMobile && '100%'}>
    //           Go
    //         </Button>
    //       </Flex>
    //       {showAmountInput && (
    //         <Flex flexDirection="column" mt="1em" gap="1em">
    //           <Text color="textSubtle" fontSize="18px" textAlign="start">
    //             Enter Amount
    //           </Text>
    //           <Input
    //             value={amount}
    //             onChange={(e) => setAmount(e.target.value)}
    //             placeholder="Enter the amount to burn"
    //             type="number"
    //           />
    //           <Input
    //             value={projectName}
    //             onChange={(e) => {
    //               const { value } = e.target
    //               if (/^[a-zA-Z\s]*$/.test(value)) {
    //                 setProjectName(value)
    //               }
    //             }}
    //             placeholder="Enter the project name"
    //             type="text"
    //           />
    //           <Input
    //             value={projectName}
    //             onChange={(e) => {
    //               const { value } = e.target
    //               if (/^[a-zA-Z\s]*$/.test(value)) {
    //                 setProjectName(value)
    //               }
    //             }}
    //             placeholder="Enter the project image url"
    //             type="text"
    //           />
    //           <Flex alignItems="center" justifyContent="center" width="100%">
    //             <Button onClick={() => writeClaimResult()}>Place Bid</Button>
    //           </Flex>
    //         </Flex>
    //       )}
    //       {error && (
    //         <Text color="failure" fontSize="18px" textAlign="start">
    //           {error}
    //         </Text>
    //       )}
    //     </Flex>
    //   </Page>
    // </>
    <PageHeader
      background={
        isDark
          ? 'linear-gradient(135deg, #000000 0%, #141317 100%)'
          : 'linear-gradient(160deg, #0093E9 0%, #80D0C7 100%)'
      }
    >
      <FarmFlexWrapper justifyContent="center">
        <Box>
          <FarmH1 as="h1" scale="xxl" color="secondary" mb="24px">
            {t('AUCTIONS')}
          </FarmH1>
          <FarmH2 scale="lg" color="text">
            {t('COMING SOON.')}
          </FarmH2>
          {/* <CountdownTimer startTime={startTime} endTime={endTime} /> */}
        </Box>
      </FarmFlexWrapper>
    </PageHeader>
  )
}

export default Auctions

const CountdownTimer = ({ startTime, endTime }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(startTime, endTime))
  const { isDark } = useTheme()

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(startTime, endTime)
      setTimeLeft(newTimeLeft)
    }, 1000)

    return () => clearInterval(timer) // Cleanup interval on component unmount
  }, [startTime, endTime])

  return (
    <Timer color={isDark ? ' #ad930c' : '#f00'}>
      <div>{timeLeft?.days}</div>
      <div>:</div>
      <div>{timeLeft?.hours}</div>
      <div>:</div>
      <div>{timeLeft?.minutes}</div>
      <div>:</div>
      <div>{timeLeft?.seconds}</div>
    </Timer>
  )
}

const FAQ = () => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleFAQ = () => {
    setIsOpen(!isOpen)
  }

  return (
    <>
      <Flex alignItems="center" flexDirection="column" mt="1em" gap="1em">
        <HelpIcon style={{ width: '48px', height: '48px', cursor: 'pointer' }} onClick={toggleFAQ}>
          {' '}
          {isOpen ? 'Hide Instructions' : 'Show Instructions'}
        </HelpIcon>
      </Flex>
      <GuideBox isOpen={isOpen}>
        <h2>How the Auction Works: A Simple Guide for Users</h2>

        <p>
          <strong>1. Connecting Your Wallet</strong>
        </p>
        <p>
          To get started, you&apos;ll need to connect your crypto wallet (like MetaMask) to our platform. This lets you
          bid on items using your tokens.
        </p>

        <p>
          <strong>2. Understanding the Auction</strong>
        </p>
        <ul>
          <li>
            <strong>Auction Rounds</strong>: Our auctions happen in rounds. Each round has a start time and an end time.
          </li>
          <li>
            <strong>Bidding</strong>: During each round, you can place bids using your tokens. The highest bid at the
            end of the round wins!
          </li>
        </ul>

        <p>
          <strong>3. Placing a Bid</strong>
        </p>
        <ul>
          <li>
            <strong>Step 1</strong>: Enter the amount of tokens you want to bid. Remember, your bid needs to be higher
            than the current highest bid to be accepted.
          </li>
          <li>
            <strong>Step 2</strong>: If it&apos;s your first bid in this round, you&apos;ll need to tell us about your
            project by providing a name and a link. This helps us know what you&apos;re bidding for!
          </li>
          <li>
            <strong>Step 3</strong>: Hit the &quot;Place Bid&quot; button, and confirm the transaction in your wallet.
            That&apos;s it&mdash;you&apos;ve placed your bid!
          </li>
        </ul>

        <p>
          <strong>4. What Happens Next?</strong>
        </p>
        <ul>
          <li>
            <strong>Winning</strong>: If you have the highest bid when the round ends, you win! Your tokens will be sent
            to a special address where they&apos;re taken out of circulation.
          </li>
          <li>
            <strong>If You Don&apos;t Win</strong>: Don&apos;t worry&mdash;your tokens will be returned to you, minus
            any fees.
          </li>
        </ul>

        <p>
          <strong>5. Checking Your Bids</strong>
        </p>
        <p>
          You can always check how your bids are doing during the round. See if you&apos;re still the highest bidder,
          and how much time is left before the round ends.
        </p>

        <p>
          <strong>6. Important Notes</strong>
        </p>
        <ul>
          <li>Make sure your bid is higher than the current highest bid.</li>
          <li>The auction round needs to be active for you to place a bid, so keep an eye on the timer!</li>
          <li>Once you&apos;ve provided your project details, you won&apos;t need to do it again in the same round.</li>
        </ul>

        <p>
          <strong>Need Help?</strong>
        </p>
        <p>
          If you have any questions or run into issues, feel free to reach out to our support team. We&apos;re here to
          help!
        </p>
      </GuideBox>
    </>
  )
}
